import { WizDropItemEvent } from './../../../directives/behavior/drag-drop/wiz-drag-drop.model';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Input,
  NgZone,
  OnDestroy,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { WizDraggableItem, WIZ_DRAGGABLE_COMPONENT } from '../../../directives/behavior/drag-drop/wiz-drag-drop.model';
import { WizBaseCollector } from '../../core/wiz-base-collector';
import { BASE_COMPONENT, WizBaseComponent } from './../../core/wiz-base-component';
import { TreeItemWrapper } from './tree-item.wrapper';
import { IWIZ_COMPONENT} from '../../../interfaces/iwiz-component';
import { WDSChanges, WIZ_BOOLEAN, WIZ_EVENT_TYPE } from '@wizeoo/akm-core-lib';
import { WizEvent } from '../../../events/wiz-event';
import { WizEventManager } from '../../../events/wiz-event.manager';

@Component({
  selector: 'wiz-tree',
  templateUrl: './wiz-tree.html',
  styleUrls: ['./wiz-tree.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: BASE_COMPONENT, useExisting: forwardRef(() => WizTree) },
    { provide: WizBaseComponent, useExisting: forwardRef(() => WizTree) },
    { provide: IWIZ_COMPONENT, useExisting: forwardRef(() => WizTree) },
    {
      provide: WIZ_DRAGGABLE_COMPONENT,
      useExisting: forwardRef(() => WizTree),
    },
  ],
})
export class WizTree<T> extends WizBaseCollector<T> implements AfterViewChecked, AfterViewInit, OnDestroy {
  // le composant input véritable
  @ViewChild('wizCdkList', { static: true, read: ElementRef })
  protected wizCdkList: ElementRef;

  /** Constructor with translation engine
   * @param cdref: ChangeDetectorRef
   * @param elementRef: the dom of the component
   * @param renderer: object that will allow the interaction between the component and the DOM
   */
  constructor(cdref: ChangeDetectorRef, elementRef: ElementRef, renderer: Renderer2, public ngZone: NgZone) {
    super(cdref, elementRef, renderer, ngZone);
  }

  // ####
  // ####
  // #### rendus ####
  // ####
  // ####

  /**
   * the height of the cdk-virtual-scroll
   *
   * this properties helps to addapt the virtual scroll height to its parent and is used to set the minBufferpx
   * minBufferpx is the minimum amount of content buffer (in pixels) that the viewport must render.
   */
  public wizVirtualScrollHeight: number = 400;

  /** Get label to display */
  public getLabel(flatNode: TreeItemWrapper<T>) {
    if (flatNode.source && flatNode.source[this.wizLabelField]) {
      return flatNode.source[this.wizLabelField];
    }
    return '';
  }

  /**
   * Does the same thing as the actionsTemplate except you can just give the component class and we will work with it
   * As for the implementation we require that your component implements IWizItemRenderer
   */
  @Input() public set actionsRenderer(value: any) {
    this._actionsRenderer = value;
    this.cdref.markForCheck();
  }
  public get actionsRenderer(): any {
    return this._actionsRenderer;
  }
  private _actionsRenderer: any;

  // ####
  // ####
  // #### mécanique interne ####
  // ####
  // ####

  /** sur fin de scroll par clavier, refresh visuel (pour marque de focus) */
  public refreshAfterScroll(e): void {
    this.cdref.markForCheck();
  }

  /** override pour éviter nullPointer sur addSelectedItem */
  public getWizValue(): any {
    if (!super.getWizValue()) {
      // fix nullPointer si pas de sélection initiale
      this.setWizValue(new Map<any, any>());
    }
    return super.getWizValue();
  }

  onProviderChanges(changes: WDSChanges): void {
    if (changes && (changes.type === 'treeInit' || changes.type === 'treeRefreshed' || !this._dataSource)) {
      this._dataSource = this.wizDataProvider.getTreeWorkedSource();
    }

    super.onProviderChanges(changes);
  }

  _dataSource: TreeItemWrapper<T>[];

  // public pour utilisation html (affichage des checkbox sur le tree)
  public get multipleSelection(): boolean {
    return this.wizDataProvider && this.wizDataProvider.selectionMode === 'multiple';
  }

  // ####
  // ####
  // #### gestion des nodes ####
  // ####
  // ####

  /** flag pour gestion de l'icone des noeuds */
  public isNodeExpanded(tiw: TreeItemWrapper<T>): boolean {
    return this.wizDataProvider && this.wizDataProvider.isNodeExpanded(tiw);
  }

  /**
   * clic sur l'icon du noeud
   * Expand or collpase node depending of status
   */
  public nodeIconClick(e: MouseEvent, tiw: TreeItemWrapper<T>) {
    e.stopImmediatePropagation();
    e.stopPropagation(); // interception du clic pour ne pas sélectionner l'item
    e.preventDefault();
    this.wizDataProvider.toggleNode(tiw);
  }

  /** fleches gauche / droite, ouverture / fermeture des noeuds */
  public openCloseFromKey(idx: number, doOpen: boolean): void {
    // collecte de l'item
    const targetTiw: TreeItemWrapper<T> = this.wizDataProvider.getItemWrapper(this.wizDataProvider.getItem(idx));
    // console.log('openCloseFromKey',idx,doOpen,targetTiw.hasChildren);
    if (targetTiw.hasChildren) {
      if (doOpen) {
        this.wizDataProvider.expandNode(targetTiw.source);
      } else {
        this.wizDataProvider.collapseNode(targetTiw.source);
      }
    }
  }

  /**
   * Si l'élément n'est pas visible au moment ou il est demandé, on le met en pending
   * Il sera rendu visible une fois la liste rafraichie
   */
  private _pendingScrollTo: any;

  /**
   * @inheritdoc
   */
  public ensureIndexIsVisible(pElement: number | T) {
    // coercion de l'index et de l'item
    const { idx, elem } = isNaN(pElement as any)
      ? {
          idx: this.wizDataProvider.getItemIndex(pElement as T),
          elem: pElement as any,
        }
      : {
          idx: pElement as number,
          elem: this.wizDataProvider.getItem(pElement as number) as any,
        };

    // on récupère le wrapper associé à l'élément
    const wrapper: TreeItemWrapper<T> = this.wizDataProvider.getItemWrapper(elem);

    if (!!wrapper) {
      // on s'assure que l'élément "peut" être visible dans la liste d'affichage.
      // i.e que lui ou son parent est en statut expanded
      this._pendingScrollTo = wrapper; // le parent ou l'item lui-même si pas de parent (root)
      if (!!wrapper.getParent() && !this.wizDataProvider.isNodeExpanded(wrapper.getParent())) {
        this.wizDataProvider.expandNodeRecursively(this._pendingScrollTo);
      }
    }
  }

  ngAfterViewChecked() {
    if (!!this._pendingScrollTo) {
      // scroll to en attente
      const item: any = this._pendingScrollTo;
      this._pendingScrollTo = undefined;

      // ordonne au virtual scroll de scroller vers l'item demandé
      this.wizVirtualScroll.scrollToIndex(this.wizDataProvider.getItemIndex(item));
    }
  }

  private $roVirtualScroll: ResizeObserver;
  private _virtualScrollContainterH: number = 0;

  ngAfterViewInit() {
    super.ngAfterViewInit();

    this.$roVirtualScroll = new ResizeObserver((entries, observer) => {
      const entry = entries[0];
      const entryHeight: number = entry.contentRect.height;
      if (entryHeight !== this._virtualScrollContainterH) {
        this.wizVirtualScroll.checkViewportSize();
      }
      this._virtualScrollContainterH = entry.contentRect.width;
    });

    this.$roVirtualScroll.observe(this.wizVirtualScroll.elementRef.nativeElement);
  }

  ngOnDestroy() {
    this.$roVirtualScroll.disconnect();
  }

  // ####
  // ####
  // #### interactions sélections ####
  // ####
  // ####

  /** état de la checkBox en fonction de l'état de l'item */
  public getCheckboxState(item: TreeItemWrapper<T>): WIZ_BOOLEAN {
    let result: WIZ_BOOLEAN = WIZ_BOOLEAN.FALSE;
    if (this.wizDataProvider.isSelected(item.source)) {
      result = WIZ_BOOLEAN.TRUE;
    } else if (this.wizDataProvider.isPartialSelected(item)) {
      result = WIZ_BOOLEAN.UNKNOWN;
    }
    // console.log('getCheckboxState',item.source, result);
    return result;
  }

  /** détermine si l'élément est sélectionné */
  public isSelected(item: TreeItemWrapper<T>): boolean {
    return this.wizDataProvider.isSelected(item.source);
  }

  /**
   * @inheritdoc
   */
  protected onSelectionChanging(pEvent: WizEvent): void {
    // override de la méthode de selectionChanging pour gestion avec la hierarchicalDatasource, on appelle pas la même méthode
    if (!pEvent.defaultPrevented) {
      this.wizDataProvider.toggleHierarchical(pEvent.value);
    }
  }

  /**
   * clic sur la checkBox. Arrêt de l'event.
   * Traitement de l'item (ajout à la pile de sélection)
   */
  public onCheckSelected(e: WizEvent, item: TreeItemWrapper<T>): void {
    // console.log('itemSelectionToggle');
    e.stopPropagation(); // interception du clic pour ne pas sélectionner l'item
    e.preventDefault();
    // this.innerSelectUpdate(item);
    super.doSelect(item.source);
  }

  /**
   * sélection d'un item, par clic / par space
   * UNIQUEMENT en mode single (sinon, space)
   */
  public onRowSelected(idx: number): void {
    // seulement en mode mono. Sinon, géré par la check / keydown.space
    if (this.wizDataProvider.selectionMode === 'single') {
      super.doSelect(this.wizDataProvider.getItem(idx, true));
    }
  }

  /** touche space : en mode multi, select */
  public toggleNodeFromSpace(idx: number): void {
    if (this.wizDataProvider.selectionMode === 'multiple') {
      super.doSelect(this.wizDataProvider.getItem(idx, true));
    }
  }

  /** double click */
  public onDoubleClick(idx: number): void {
    // seulement si différent de none
    if (this.wizDataProvider.selectionMode !== 'none') {
      const target: T = this.wizDataProvider.getItem(idx, true); // collecte de l'item
      super.emitDoubleClick(target);
    }
  }

  /** donne le focus */
  public wizFocus(): void {
    this.wizCdkList.nativeElement.focus();
  }

  /**
   * @Override : pas de relache sur un enfant
   * au survol, appelé par le dndManager, fonction de contrôle
   * return true si drop autorisé
   */
  checkDragOver({ item, targetPosition }: WizDropItemEvent): boolean {
    const overItem: TreeItemWrapper<T> = this._getTreeItem(targetPosition);
    return !this.wizDataProvider.isAncestor(item.data as TreeItemWrapper<T>, overItem);
  }

  private _getTreeItem(pIndex: number): TreeItemWrapper<T> {
    return this.wizDataProvider.getItem(pIndex) as any as TreeItemWrapper<T>;
  }

  /**
   * Ajout d'un item depuis un drag'n'drop
   */
  public onAddItemAfterDrag({ item, targetPosition }: WizDropItemEvent): void {
    const overItem: TreeItemWrapper<T> = this._getTreeItem(targetPosition);
    this.wizDataProvider.addChild(item.data as T, overItem);
    WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.DRAG_DROPPED, this, item));
  }

  /**
   * Self Drag'n'drop. Changement de place dans la collection (le array source)
   */
  public onMoveItemAfterDrag({ item, targetPosition }: WizDropItemEvent): void {
    const overItem: TreeItemWrapper<T> = this._getTreeItem(targetPosition);
    this.wizDataProvider.addChild(item.data as T, overItem);
    WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.DRAG_MOVED, this, item));
  }

  /**
   * Suppression de l'item de la collection d'origine sur move (target # origin)
   */
  public onRemoveItemAfterDrag({ data }: WizDraggableItem): void {
    this.wizDataProvider.removeChild(data as T);
  }
}
